import React, { useState, useRef } from 'react';
import {
  TrendingDown, TrendingUp, Minus, ChevronLeft, ChevronRight,
  Tag, Share2, Copy, MessageCircle,
  BellRing, Loader, Eye, Maximize2,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getSourceBadgeClass, getSourceName } from '../../lib/utils';
import { toast } from 'sonner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('it-CH', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function formatDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('it-CH', { day: '2-digit', month: '2-digit' });
}

export const SOURCE_COLORS = {
  tutti: 'hsl(173 58% 39%)',
  anibis: 'hsl(23 82% 34%)',
  ricardo: 'hsl(220 72% 38%)',
  shpock: 'hsl(140 70% 28%)',
  ebay: 'hsl(40 92% 27%)',
};

// ─── Price Tooltip ────────────────────────────────────────────────────────────
export function PriceTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="text-muted-foreground mb-1">{label}</p>
      <p className="font-semibold text-foreground">CHF {payload[0].value?.toLocaleString('it-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
    </div>
  );
}

// ─── Price Trend ──────────────────────────────────────────────────────────────
export function PriceTrend({ history }) {
  const { t } = useTranslation();
  if (!history || history.length < 2) return null;
  const first = history[0].price_chf;
  const last = history[history.length - 1].price_chf;
  const diff = last - first;
  const pct = first > 0 ? Math.round((diff / first) * 100) : 0;
  if (Math.abs(pct) < 1) return <span className="text-xs text-muted-foreground flex items-center gap-1"><Minus size={12} /> {t('priceStable')}</span>;
  if (diff < 0) return (
    <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
      <TrendingDown size={12} /> -{Math.abs(pct)}% {t('fromFirstRecord')}
    </span>
  );
  return (
    <span className="text-xs text-rose-500 flex items-center gap-1 font-medium">
      <TrendingUp size={12} /> +{pct}% {t('fromFirstRecord')}
    </span>
  );
}

// ─── Image Gallery ────────────────────────────────────────────────────────────
export function ListingImage({ listing, allImages, currentIdx, onPrev, onNext, onOpen }) {
  const [imgError, setImgError] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const currentImg = allImages.length > 0 ? allImages[currentIdx] : listing.image_url;
  const hasMultiple = allImages.length > 1;

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    // Only horizontal swipes (ignore vertical scroll) and min threshold 40px
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) onNext(); else onPrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  if (imgError || !currentImg) {
    return (
      <div className="w-full h-56 bg-muted rounded-xl flex items-center justify-center">
        <Tag size={36} className="text-muted-foreground/30" />
      </div>
    );
  }
  return (
    <div
      className={`relative w-full h-56 rounded-xl overflow-hidden bg-muted group touch-pan-y ${onOpen ? 'cursor-zoom-in' : ''}`}
      onTouchStart={hasMultiple ? handleTouchStart : undefined}
      onTouchEnd={hasMultiple ? handleTouchEnd : undefined}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      data-testid="quick-view-image-gallery"
    >
      <img
        key={currentImg}
        src={currentImg}
        alt="listing"
        onError={() => setImgError(true)}
        className="w-full h-full object-contain transition-opacity duration-200 select-none pointer-events-none"
        loading="lazy"
        draggable="false"
      />
      {onOpen && (
        <span className="absolute bottom-2 right-2 bg-black/50 text-white rounded-full p-1.5 pointer-events-none" data-testid="qv-image-expand-hint">
          <Maximize2 size={14} />
        </span>
      )}
      {hasMultiple && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} data-testid="qv-image-prev" className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronLeft size={16} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} data-testid="qv-image-next" className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {allImages.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIdx ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
          <span className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
            {currentIdx + 1} / {allImages.length}
          </span>
        </>
      )}
    </div>
  );
}

// ─── Metadata Row ─────────────────────────────────────────────────────────────
export function MetaRow({ icon: Icon, label, value, valueClass = '' }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border/40 last:border-0">
      <div className="shrink-0 w-7 h-7 rounded-lg bg-muted flex items-center justify-center mt-0.5">
        <Icon size={13} className="text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
        <p className={`text-sm font-medium break-words ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}

// ─── Share Buttons ─────────────────────────────────────────────────────────────
// Link condivisi: sempre il dominio ufficiale (in produzione l'API gira anche
// su un indirizzo tecnico che non deve mai finire nei messaggi).
const SHARE_BASE = 'https://scanmarket.ch';
const API = process.env.REACT_APP_BACKEND_URL;

function buildShareUrl(listing, channel) {
  const src = listing?.source;
  const id = listing?.source_listing_id;
  if (!src || !id) return listing?.listing_url || '';
  const params = new URLSearchParams({
    utm_source: channel,
    utm_medium: 'share',
    utm_campaign: 'quickview',
    lang: ((typeof localStorage !== 'undefined' && localStorage.getItem('scanmarket_language')) || 'de').split('-')[0],
  });
  return `${SHARE_BASE}/api/r/${encodeURIComponent(src)}/${encodeURIComponent(id)}?${params.toString()}`;
}

async function logShare(listing, channel) {
  try {
    let sessionId = null;
    try { sessionId = localStorage.getItem('scanmarket_session_id'); } catch {}
    await fetch(`${API}/api/shares/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: listing?.source,
        source_listing_id: String(listing?.source_listing_id || ''),
        channel,
        session_id: sessionId,
        lang: (typeof navigator !== 'undefined' && navigator.language) || null,
      }),
    });
  } catch { /* best-effort */ }
}

export function ShareButtons({ listing }) {
  const { t } = useTranslation();
  const title = listing?.title || '';
  const priceStr = listing?.price_chf != null ? `CHF ${listing.price_chf}` : '';
  const shareText = `${title}${priceStr ? ` — ${priceStr}` : ''} su ${getSourceName(listing?.source)} | ScanMarket`;

  const handleCopyLink = async () => {
    const url = buildShareUrl(listing, 'copy_link');
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('linkCopied'));
      logShare(listing, 'copy_link');
    } catch { toast.error(t('copyFailed')); }
  };
  const handleWhatsApp = () => {
    const url = buildShareUrl(listing, 'whatsapp');
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n' + url)}`, '_blank');
    logShare(listing, 'whatsapp');
  };
  const handleTelegram = () => {
    const url = buildShareUrl(listing, 'telegram');
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`, '_blank');
    logShare(listing, 'telegram');
  };
  const handleNativeShare = async () => {
    if (!navigator.share) return;
    const url = buildShareUrl(listing, 'native');
    try {
      await navigator.share({ title: listing?.title, text: shareText, url });
      logShare(listing, 'native');
    } catch { /* user cancelled */ }
  };

  return (
    <div className="flex items-center gap-1.5" data-testid="quick-view-share-buttons">
      <button onClick={handleWhatsApp} data-testid="share-whatsapp" className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-green-500/30 text-green-700 dark:text-green-400 hover:bg-green-500/10 active:scale-95 transition-all" title="Condividi su WhatsApp">
        <MessageCircle size={13} /><span className="hidden sm:inline">WhatsApp</span>
      </button>
      <button onClick={handleTelegram} data-testid="share-telegram" className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 active:scale-95 transition-all" title="Condividi su Telegram">
        <Share2 size={13} /><span className="hidden sm:inline">Telegram</span>
      </button>
      <button onClick={handleCopyLink} data-testid="share-copy-link" className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground active:scale-95 transition-all" title="Copia link">
        <Copy size={13} /><span className="hidden sm:inline">Copia</span>
      </button>
      {typeof navigator.share === 'function' && (
        <button onClick={handleNativeShare} data-testid="share-native" className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground active:scale-95 transition-all" title="Condividi">
          <Share2 size={13} />
        </button>
      )}
    </div>
  );
}

// ─── Price Watch Form ──────────────────────────────────────────────────────────
export function PriceWatchForm({ listing, priceWatchThreshold, setPriceWatchThreshold, priceWatchEmail, setPriceWatchEmail, priceWatchSet, priceWatchLoading, onSubmit }) {
  const { t } = useTranslation();
  if (!listing.price_chf || listing.price_chf <= 0) return null;
  return (
    <div data-testid="quick-view-price-watch" className="rounded-xl border border-border/50 bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-2.5">
        <BellRing size={14} className="text-amber-500" />
        <h3 className="text-sm font-semibold">{t('priceWatchTitle')}</h3>
      </div>
      {priceWatchSet ? (
        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
          <span>{t('priceWatchSet', { price: priceWatchThreshold })}</span>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">CHF</span>
              <input type="number" value={priceWatchThreshold} onChange={e => setPriceWatchThreshold(e.target.value)}
                placeholder={listing.price_chf ? String(Math.round(listing.price_chf * 0.8)) : '100'}
                min="1" step="1" data-testid="price-watch-threshold"
                className="w-full h-9 pl-10 pr-3 text-sm border border-border/70 rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-foreground/20" />
            </div>
            <button type="submit" disabled={priceWatchLoading} data-testid="price-watch-submit"
              className="px-3 h-9 text-xs font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 active:scale-95 transition-all disabled:opacity-50 shrink-0">
              {priceWatchLoading ? <Loader size={12} className="animate-spin" /> : t('priceWatchNotify')}
            </button>
          </div>
          <input type="email" value={priceWatchEmail} onChange={e => setPriceWatchEmail(e.target.value)}
            placeholder={t('emailOptional')} data-testid="price-watch-email"
            className="w-full h-8 px-3 text-xs border border-border/50 rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-foreground/20" />
        </form>
      )}
    </div>
  );
}

// ─── Similar Listings (carousel swipeable) ────────────────────────────────────
export function SimilarListings({ similarListings, similarLoading }) {
  const { t } = useTranslation();
  if (!similarLoading && similarListings.length === 0) return null;
  return (
    <div data-testid="quick-view-similar">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        {t('similarListings')}
      </h3>
      {similarLoading ? (
        <div className="flex gap-2 overflow-x-hidden">
          {[0,1,2].map(i => <div key={i} className="h-44 w-[160px] shrink-0 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div
          className="flex gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-5 px-5 pb-1"
          style={{ scrollPaddingLeft: '20px', scrollPaddingRight: '20px' }}
        >
          {similarListings.slice(0, 8).map((item, idx) => (
            <a
              key={`${item.source}_${item.source_listing_id}_${idx}`}
              href={item.listing_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`similar-listing-${idx}`}
              className="group snap-start shrink-0 w-[160px] flex flex-col rounded-xl border border-border/50 bg-card overflow-hidden hover:border-border hover:shadow-sm transition-all"
            >
              <div className="h-28 bg-muted overflow-hidden">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" loading="lazy" referrerPolicy="no-referrer" draggable="false" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Eye size={16} className="text-muted-foreground/30" /></div>
                )}
              </div>
              <div className="p-2 flex flex-col gap-0.5">
                <div className="flex items-center gap-1">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${getSourceBadgeClass(item.source)}`}>{getSourceName(item.source)}</span>
                  {item.deal_score >= 15 && <span className="text-[9px] px-1 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-semibold">-{Math.round(item.deal_score)}%</span>}
                </div>
                <p className="text-xs font-medium line-clamp-2 leading-tight">{item.title}</p>
                <p className="text-sm font-bold tabular-nums">{item.price_chf != null ? `CHF ${item.price_chf?.toLocaleString('it-CH')}` : ''}</p>
                {item.city && <p className="text-[10px] text-muted-foreground truncate">{item.city}</p>}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Price History Chart ───────────────────────────────────────────────────────
export function PriceHistoryChart({ priceHistory, historyLoading, sourceColor, currentPrice }) {
  const { t } = useTranslation();
  const priceMin = priceHistory.length ? Math.min(...priceHistory.map(h => h.price_chf)) : null;
  const priceMax = priceHistory.length ? Math.max(...priceHistory.map(h => h.price_chf)) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('priceHistory')}</h3>
        {priceHistory.length >= 2 && <PriceTrend history={priceHistory} />}
      </div>
      {historyLoading ? (
        <div className="h-28 bg-muted rounded-xl animate-pulse" />
      ) : priceHistory.length >= 2 ? (
        <div data-testid="quick-view-price-chart" className="bg-muted/30 border border-border/40 rounded-xl p-3">
          <div className="flex gap-3 mb-3 text-xs">
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><TrendingDown size={11} /> {t('minLabel')}: CHF {priceMin?.toLocaleString('it-CH')}</span>
            <span className="flex items-center gap-1 text-rose-500"><TrendingUp size={11} /> {t('maxLabel')}: CHF {priceMax?.toLocaleString('it-CH')}</span>
          </div>
          <ResponsiveContainer width="100%" height={130}>
            <LineChart data={priceHistory} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} tickFormatter={v => `${v}`} domain={['auto', 'auto']} width={45} />
              <Tooltip content={<PriceTooltip />} />
              {currentPrice != null && <ReferenceLine y={currentPrice} stroke={sourceColor} strokeDasharray="4 3" strokeOpacity={0.7} />}
              <Line type="monotone" dataKey="price_chf" stroke={sourceColor} strokeWidth={2} dot={{ fill: sourceColor, r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-muted/30 border border-border/40 rounded-xl px-4 py-3">
          {priceHistory.length === 1 ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{t('priceRecordedOn')}</p>
                <span className="text-sm font-semibold">CHF {priceHistory[0].price_chf?.toLocaleString('it-CH')}</span>
              </div>
              <p className="text-xs text-muted-foreground/70">{t('priceGraphSoon')} {t('priceCheckedOnOpen')}</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-2">📈 {t('priceHistoryCollecting')}</p>
          )}
        </div>
      )}
    </div>
  );
}
